import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { api } from "@/api";
import { Star, Quote } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Reviews() {
  const { t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data));
  useEffect(() => {
    load();
  }, []);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
              {t.reviews.eyebrow}
            </div>
            <h2 className="font-heading mt-3 text-4xl sm:text-5xl font-black tracking-tighter">
              {t.reviews.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.reviews.subtitle}</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="leave-review-btn"
                className="bg-brand-blue hover:bg-brand-blue-light rounded-none font-mono-accent uppercase text-xs tracking-widest px-6 py-5 border border-brand-blue-light/50 self-start"
              >
                {t.reviews.leave}
              </Button>
            </DialogTrigger>
            <ReviewDialog onSubmitted={() => { setDialogOpen(false); load(); }} />
          </Dialog>
        </div>

        {reviews.length === 0 ? (
          <div className="mt-12 border border-dashed border-white/10 p-12 text-center text-zinc-500 font-mono-accent uppercase text-xs">
            {t.reviews.empty}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewCard({ review }) {
  return (
    <article
      data-testid={`review-card-${review.id}`}
      className="relative bg-zinc-900/60 border border-white/10 hover:border-brand-blue/40 p-7 transition-colors"
    >
      <Quote size={24} className="text-brand-blue-light opacity-70" />
      <p className="mt-4 text-zinc-200 leading-relaxed">"{review.comment}"</p>
      <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
        <div className="font-heading font-bold">{review.name}</div>
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={14}
              className={i < review.rating ? "fill-brand-blue-light text-brand-blue-light" : "text-white/10"}
            />
          ))}
        </div>
      </div>
    </article>
  );
}

function ReviewDialog({ onSubmitted }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success(t.reviews.success);
      onSubmitted();
      setForm({ name: "", rating: 5, comment: "" });
    } catch {
      toast.error(t.order.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent data-testid="review-modal" className="bg-[#0d0d0d] border-white/10 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl font-black">{t.reviews.leave}</DialogTitle>
        <DialogDescription className="text-zinc-400">{t.reviews.leave_desc}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
            {t.reviews.rname}
          </Label>
          <Input
            required
            data-testid="review-form-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
          />
        </div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
            {t.reviews.rrating}
          </Label>
          <div className="mt-2 flex gap-1" data-testid="review-form-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setForm({ ...form, rating: n })}
                aria-label={`rating-${n}`}
                data-testid={`review-star-${n}`}
              >
                <Star
                  size={26}
                  className={n <= form.rating ? "fill-brand-blue-light text-brand-blue-light" : "text-white/20"}
                />
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
            {t.reviews.rcomment}
          </Label>
          <Textarea
            required
            rows={4}
            data-testid="review-form-comment"
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="review-form-submit"
          className="w-full bg-brand-blue hover:bg-brand-blue-light text-white font-mono-accent uppercase tracking-widest text-xs py-6 rounded-none"
        >
          {submitting ? t.reviews.submitting : t.reviews.submit}
        </Button>
      </form>
    </DialogContent>
  );
}
